import axios from 'axios';
import { RiskLevel, TrendDirection } from '../types/enums';
import { TimelineData } from '../types/timeline';
import { API_BASE } from './config';

// Same-origin in production; only use REACT_APP_API_URL if explicitly set
// All requests prefix with a single API base
const API_URL = API_BASE;

export interface PublicDashboardData {
  lastUpdated: string;
  overallRisk: {
    /** null (with hasLiveData=false) when the range has no live analyses */
    score: number | null;
    level: RiskLevel | null;
    trend: TrendDirection;
    change24h: number;
    hasLiveData?: boolean;
  };
  categoryBreakdown: Array<{
    code: string;
    name: string;
    score: number;
    trend: 'up' | 'down' | 'stable';
    color: string;
  }>;
  riskHistory: Array<{
    date: string;
    score: number;
  }>;
  criticalArticles: Array<{
    id: string;
    title: string;
    url: string;
    publishedAt: string;
    source: string;
    overallRiskScore: number;
    keyPoints: string[];
    categories: string[];
  }>;
  stats: {
    articlesAnalyzedToday: number;
    articlesAnalyzedTotal: number;
    sourcesMonitored: number;
    lastAnalysis: string;
  };
}

class PublicApiService {
  async getSeoMeta(path: string, locale: string): Promise<any> {
    const params = new URLSearchParams({ path, locale });
    const res = await axios.get(`${API_URL}/api/public/seo?${params.toString()}`);
    return res.data;
  }
  async getDashboardData(startDate?: string, endDate?: string, categories?: string[], country: string = 'EST'): Promise<PublicDashboardData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (categories && categories.length > 0) params.append('categories', categories.join(','));
      if (country) params.append('country', country);
      const qs = params.toString();
      const response = await axios.get<PublicDashboardData>(`${API_URL}/api/public/dashboard${qs ? `?${qs}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }

  async getTimelineData(
    startDate?: string,
    endDate?: string,
    categories?: string[],
    country: string = 'EST'
  ): Promise<TimelineData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (categories && categories.length > 0) params.append('categories', categories.join(','));
      params.append('country', country);

      const response = await axios.get<TimelineData>(`${API_URL}/api/public/timeline?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch timeline data:', error);
      throw error;
    }
  }
}

export const publicApi = new PublicApiService();
